import {
  Cartographic,
  defined,
  ScreenSpaceEventHandler,
  ScreenSpaceEventType,
} from "@cesium/engine";
import PointEntity from "../edit/PointEntity";
import Utils from "../basic/Utils";

/**
 * 점 엔티티 생성을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input class for creating point entities using mouse/keyboard input.
 *
 * @alias InteractionPointEntity
 * @constructor
 *
 * @param {object} pointOption 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Function} [pointOption.addCallback] 점을 생성할 때 실행되는 콜백 함수. <br>Callback function executed when creating a point
 * @param {PointEntity} [pointOption.PointEntity] 점 엔티티 표시 클래스.</br> Point entity display class.
 * @example
 * // 샌드캐슬 예제 'DiTAP - Point Entity' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const pointEntity = new Ditap.PointEntity(viewer);
 *
 * // InteractionPointEntity on/off 실행 시 PointEntity 초기화 필수
 * // PointEntity 초기화 방법
 * // 1. InteractionPointEntity 생성 후, PointEntity, callback 함수 대입
 * const interactionPointEntity = new Ditap.InteractionPointEntity();
 * interactionPointEntity.pointEntity = pointEntity;
 * interactionPointEntity.addCallback = () => {};
 *
 * // 2. InteractionPointEntity 생성 시, pointOption 지정
 * const pointOption = {
 *    pointEntity : pointEntity,
 *    addCallback: () => {}
 * }
 * const interactionPointEntity = new Ditap.InteractionPointEntity(InteractionPointEntity);
 */
class InteractionPointEntity {
  constructor(pointOption) {
    this._pointEntity = undefined; //원본 클래스
    if (pointOption && pointOption.pointEntity instanceof PointEntity) {
      this.pointEntity = pointOption.pointEntity; //원본 클래스
    }
    this._handler = undefined;
    this._moveEntities = [];
    this._infoLabelEntity = undefined;
    this._mousePosition = undefined;
    this._isOn = false; // 핸들러 실행 여부
    this._isMobile = undefined; // 모바일 여부

    if (pointOption && pointOption.addCallback instanceof Function) {
      this._addCallback = pointOption.addCallback;
    } else {
      this._addCallback = undefined;
    }
  }

  /**
   * 점 엔티티 표시 클래스.</br>
   * Point entity display class.
   * @type {PointEntity}
   */
  set pointEntity(pointEntity) {
    if (pointEntity instanceof PointEntity || pointEntity === undefined) {
      this._pointEntity = pointEntity; //원본 클래스
    } else {
      console.error("Type of parameter doesn't match");
    }
  }
  get pointEntity() {
    return this._pointEntity;
  }

  /*
   * 마우스 무브 시 생성된 엔티티 배열.</br>
   * An array of entities created by mouse movement.
   * @type {Entity}
   */
  set moveEntities(moveEntities) {
    this._moveEntities = moveEntities;
  }
  get moveEntities() {
    return this._moveEntities;
  }

  /*
   * 정보를 제공하는 라벨 요소.</br>
   * Label element providing information.
   * @type {Element}
   */
  set infoLabelEntity(infoLabelEntity) {
    this._infoLabelEntity = infoLabelEntity;
  }
  get infoLabelEntity() {
    return this._infoLabelEntity;
  }

  /*
   * 현재 마우스 좌표. </br>
   * Current mouse cartesian.
   * @type {Cartesian3}
   */
  set mousePosition(mousePosition) {
    this._mousePosition = mousePosition;
  }
  get mousePosition() {
    return this._mousePosition;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /**
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   * @readonly
   */
  set isOn(boolean) {
    this._isOn = boolean;
  }
  get isOn() {
    return this._isOn;
  }

  /**
   * 점을 생성할 때 실행되는 콜백 함수.<br>
   * Callback function executed when creating a point
   * @type {Function}
   */
  set addCallback(addCallback) {
    if (addCallback instanceof Function) {
      this._addCallback = addCallback;
    } else {
      this.addCallback = undefined;
    }
  }
  get addCallback() {
    return this._addCallback;
  }

  /**
   *
   * 점 엔티티 생성 핸들러 실행. (PointEntity 초기화 필수.)</br>
   * Execute point entity creation handler. (PointEntity initialization is required.)
   */
  on() {
    if (this.pointEntity && !this.isOn) {
      this._isMobile = Utils.detectDeviceType();
      this.isOn = true;
      this.handler = new ScreenSpaceEventHandler(
        this.pointEntity.viewer.canvas
      );

      this.mobileInfoLabel = Utils.createMobileInfoLabel(
        this.pointEntity.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭 또는 터치하여 점을 생성하세요"
      );
      this.infoLabel = Utils.createInfoLabel(
        this.pointEntity.viewer.container.querySelector(".cesium-viewer"),
        "왼 클릭하여 점을 생성하세요"
      );

      if (this._isMobile) {
        this.cancelBtn = this.mobileInfoLabel.cancelBtn;
        this.infoLabelEntity = this.mobileInfoLabel.label;
        this.createMobileEvent();
        this.infoLabel.style.display = "none";
      } else {
        this.infoLabelEntity = this.infoLabel;
        this.createMouseMoveHandler();
        this.mobileInfoLabel.label.style.display = "none";
      }

      this.createLeftClickHandler();
      this.createRightClickHandler();
      this.createKeyDownHandler();
      this.createResizeEvent();
    }
  }

  /**
   * 점 엔티티 생성 핸들러 종료. (PointEntity 초기화 필수.)</br>
   * End point entity creation handler. (PointEntity initialization is required.)
   */
  off() {
    if (this.pointEntity && this.isOn) {
      // // 동적 Entity 제거
      // viewer.entities.remove(this.dynamicPointEntity);
      // 도움말 라벨 제거.
      this.mobileInfoLabel.label.remove();
      this.infoLabel.remove();

      // 이벤트 제거
      if (this.handler) {
        this.handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
        this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
        this.handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);
      }
      // window.removeEventListener("mousemove", this.mousemoveEvent);
      window.removeEventListener("keydown", this.onKeyDown);
      this.removeChangeEvent();
      if (this._isMobile) {
        this.removeMobileEvent();
      }
      if (this.resizeEvent) {
        window.removeEventListener("resize", this.resizeEvent);
      }

      this.isOn = false;
      this.moveEntities = [];
      this.infoLabelEntity = undefined;
      this.mousePosition = undefined;
      this.handler = undefined;
      this.pointEntity = undefined;
      this.removeBtn = undefined;
      this._isMobile = undefined;
      this.mobileInfoLabel = undefined;
      this.infoLabel = undefined;
    }
  }

  /**
   * 모바일 전용 이벤트 추가.<br>
   * Add mobile-specific event.
   */
  createMobileEvent() {
    this.cancelBtn.addEventListener(
      "click",
      (this.cancelEvent = () => {
        this.off();
      })
    );
  }

  /**
   * 모바일 전용 이벤트 제거.<br>
   * Remove mobile-specific event.
   */
  removeMobileEvent() {
    if (this._cancelEvent) {
      this.cancelBtn.removeEventListener("click", this._cancelEvent);
    }
    this.cancelEvent = undefined;
    this.cancelBtn = undefined;
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  createResizeEvent() {
    const resizeOriginal = () => {
      if (this.isOn) {
        const isMobile = Utils.detectDeviceType();

        if (this._isMobile !== isMobile) {
          if (isMobile) {
            this._isMobile = true;

            this.cancelBtn = this.mobileInfoLabel.cancelBtn;
            this.infoLabelEntity = this.mobileInfoLabel.label;
            this.infoLabel.style.display = "none";
            this.mobileInfoLabel.label.style.display = "block";

            this.handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
            this.createMobileEvent();
          } else {
            this.removeMobileEvent();
            this._isMobile = false;
            this.infoLabelEntity = this.infoLabel;
            this.mobileInfoLabel.label.style.display = "none";
            this.infoLabel.style.display = "block";
            this.createMouseMoveHandler();
          }
        }
      }
    };

    this.resizeEvent = () => {
      resizeOriginal();
      setTimeout(resizeOriginal, 500);
    };

    window.addEventListener("resize", this.resizeEvent);
  }

  /*
   * 마우스 왼 클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse left-click event handler.
   */
  createLeftClickHandler() {
    const handler = this.handler;
    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.position, this.pointEntity.viewer);

      if (
        defined(position) &&
        Cartographic.fromCartesian(position).height >= 0
      ) {
        this.mousePosition = position;
        this.pointEntity.position = position;
        this.pointEntity.createEntity();

        if (this.addCallback && this.pointEntity) {
          this.addCallback(this.pointEntity);
        }

        // 핸들러 종료;
        this.off();
      }
    }, ScreenSpaceEventType.LEFT_CLICK);
  }

  /*
   * 마우스 이동 이벤트 핸들러 생성.</br>
   * Creates a mouse move event handler.
   */
  createMouseMoveHandler() {
    const viewer = this.pointEntity.viewer;
    const handler = this.handler;

    handler.setInputAction((e) => {
      const position = Utils.pickPosition(e.endPosition, viewer);

      if (defined(position)) {
        this.mousePosition = position;
        // 도움말 라벨 좌표 이동
        Utils.changeLabelLocation(
          viewer,
          this.mousePosition,
          this.infoLabelEntity
        );
      } else {
        this.infoLabelEntity.classList.add("hide");
      }
    }, ScreenSpaceEventType.MOUSE_MOVE);
  }

  /**
   * 생성 중 중단.</br>
   * Interrupt during creation.
   */
  abort() {
    // viewerStorage에서 삭제
    this.pointEntity?.viewer.ditapStorage.removeInstanceById(
      this.pointEntity.id
    );
    // linestringEntity 삭제
    this.pointEntity?.destroy();

    // this.pointEntity = undefined;
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    this.abort();
    this.off();
  }

  /*
   * 마우스 우클릭 이벤트 핸들러 생성.</br>
   * Creates a mouse right-click event handler.
   */
  createRightClickHandler() {
    const handler = this.handler;

    handler.setInputAction((e) => {
      this.removePoint();
    }, ScreenSpaceEventType.RIGHT_CLICK);
  }

  /*
   * 측정 중인 한 점 제거.<br>
   * Remove the point being measured.
   */
  removePoint() {
    this.off();
    this.abort();
  }

  /*
   * 키 다운 이벤트 발생 시 실행.</br>
   * Executed when a key down event occurs.
   * @param {Event} event 키 다운 이벤트 객체.</br> keydown event object
   */
  onKeyDown(event) {
    if (event.key === "Escape") {
      // 동적 Entity 제거
      this.off();
    }
  }

  /*
   * 키 다운 이벤트 핸들러 생성.</br>
   * Creates a key down event handler.
   */
  createKeyDownHandler() {
    window.addEventListener(
      "keydown",
      (e) => {
        this.onKeyDown(e);
      },
      {
        once: true,
      }
    );
  }

  /*
   * 윈도우 크기 조절 이벤트 제거.</br>
   * Remove the window resize event.
   */
  removeChangeEvent() {
    if (this.windowResizeEvent) {
      window.removeEventListener("resize", this.windowResizeEvent);
    }
  }
}
export default InteractionPointEntity;
